import { enterpriseApiSlice } from "./index";
import {
    ChPerformanceChartResponse,
    ChPerformanceSummaryResponse,
    ChPerformanceDrilldownResponse,
} from "../Model/Api/ChPerformanceObjects";

const buildChartParams = ({ companyIds, timeframe, type, orderType, startDate, endDate }) => {
    const p = new URLSearchParams({ companyIds, timeframe, orderType: orderType ?? "ONDEMAND" });
    if (type) p.append("type", type);
    if (startDate) p.append("startDate", startDate);
    if (endDate) p.append("endDate", endDate);
    return p.toString();
};

export const chPerformanceApi = enterpriseApiSlice.injectEndpoints({
    endpoints: (builder) => ({
        fetchChDurationChart: builder.query({
            query: (args) => ({
                url: `/admin/analytics/ch/v2/duration-charts?${buildChartParams(args)}`,
                method: "GET",
            }),
            extraOptions: { auth: true },
            transformResponse: (response) => new ChPerformanceChartResponse(response),
            keepUnusedDataFor: 30,
        }),
        fetchChLatenessChart: builder.query({
            query: ({ onTimeBufferMinutes = 0, ...args }) => {
                const buffer = onTimeBufferMinutes > 0 ? `&onTimeBufferMinutes=${onTimeBufferMinutes}` : "";
                return {
                    url: `/admin/analytics/ch/v2/lateness-charts?${buildChartParams(args)}${buffer}`,
                    method: "GET",
                };
            },
            extraOptions: { auth: true },
            transformResponse: (response) => new ChPerformanceChartResponse(response),
            keepUnusedDataFor: 30,
        }),
        fetchChPerformanceSummary: builder.query({
            query: ({ onTimeBufferMinutes = 0, ...args }) => {
                const buffer = onTimeBufferMinutes > 0 ? `&onTimeBufferMinutes=${onTimeBufferMinutes}` : "";
                return {
                    url: `/admin/analytics/ch/v2/performance-report?${buildChartParams(args)}${buffer}`,
                    method: "GET",
                };
            },
            extraOptions: { auth: true },
            transformResponse: (response) => new ChPerformanceSummaryResponse(response),
            keepUnusedDataFor: 30,
        }),
        fetchChDurationDrilldown: builder.query({
            query: ({ companyIds, timeframe, type, bucket, startDate, endDate, page = 1, pageSize = 25, exportAll = false }) => {
                const p = new URLSearchParams({ companyIds, timeframe, type, bucket, orderType: "ONDEMAND", page, pageSize });
                if (startDate) p.append("startDate", startDate);
                if (endDate) p.append("endDate", endDate);
                if (exportAll) p.set("export", "true");
                return { url: `/admin/analytics/ch/v2/duration-charts/drilldown?${p.toString()}`, method: "GET" };
            },
            extraOptions: { auth: true },
            transformResponse: (response) => new ChPerformanceDrilldownResponse(response),
            keepUnusedDataFor: 0,
        }),
        fetchChLatenessDrilldown: builder.query({
            query: ({ companyIds, timeframe, bucket, startDate, endDate, page = 1, pageSize = 25, exportAll = false, onTimeBufferMinutes = 0 }) => {
                const p = new URLSearchParams({ companyIds, timeframe, bucket, orderType: "ONDEMAND", page, pageSize });
                if (startDate) p.append("startDate", startDate);
                if (endDate) p.append("endDate", endDate);
                if (exportAll) p.set("export", "true");
                if (onTimeBufferMinutes > 0) p.append("onTimeBufferMinutes", String(onTimeBufferMinutes));
                return { url: `/admin/analytics/ch/v2/lateness-charts/drilldown?${p.toString()}`, method: "GET" };
            },
            extraOptions: { auth: true },
            transformResponse: (response) => new ChPerformanceDrilldownResponse(response),
            keepUnusedDataFor: 0,
        }),
    }),
    overrideExisting: false,
});

export const {
    useFetchChDurationChartQuery,
    useFetchChLatenessChartQuery,
    useFetchChPerformanceSummaryQuery,
    useFetchChDurationDrilldownQuery,
    useFetchChLatenessDrilldownQuery,
    useLazyFetchChDurationDrilldownQuery,
    useLazyFetchChLatenessDrilldownQuery,
} = chPerformanceApi;
